import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function FaqSection({ page }) {
  const [faqs, setFaqs] = useState([]);
  const { lang } = useLang();

  useEffect(() => {
    api.get("/faqs", { params: { page } }).then((res) => setFaqs(res.data));
  }, [page]);

  if (!faqs.length) return null;
  const tr = (en, es) => (lang === "es" && es ? es : en);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <section className="mx-auto max-w-3xl px-6 lg:px-12 py-16" data-testid="faq-section">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">
        {lang === "es" ? "Preguntas frecuentes" : "Frequently asked questions"}
      </h2>
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((f) => (
          <AccordionItem key={f.id} value={f.id} className="border-b border-sand-200">
            <AccordionTrigger
              data-testid={`faq-trigger-${f.id}`}
              className="text-left text-lg font-medium text-charcoal-800 hover:text-terracotta hover:no-underline py-5"
            >
              {tr(f.question, f.question_es)}
            </AccordionTrigger>
            <AccordionContent className="text-base leading-relaxed text-charcoal-600 pb-5">
              {tr(f.answer, f.answer_es)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {page !== "home" && (
        <Link to="/faq" data-testid="faq-section-all-link" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-hover transition-colors">
          {lang === "es" ? "Ver todas las preguntas" : "See all FAQs"} <ArrowRight size={15} />
        </Link>
      )}
    </section>
  );
}
